import { useState } from 'react'
import { Download, X } from 'lucide-react'
import { formatFileSize, getFileIcon } from '@/utils/fileHelpers'
import type { MessageAttachment } from '@/types/conversations'

interface MessageAttachmentsProps {
  attachments: MessageAttachment[]
}

export function MessageAttachments({ attachments }: MessageAttachmentsProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)

  if (!attachments || attachments.length === 0) return null

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {attachments.map((attachment) => {
          const isSvg = attachment.content_type === 'image/svg+xml'
          const imgSrc = attachment.small_url || attachment.url
          return (
            <div key={attachment.id}>
              {attachment.is_image && !isSvg ? (
                /* Image thumbnail */
                <div
                  className="relative group cursor-pointer"
                  onClick={() => setLightboxImage(attachment.url)}
                >
                  <img
                    src={imgSrc}
                    alt={attachment.filename}
                    className="max-w-[200px] max-h-[200px] rounded-lg border border-primary/10 hover:border-primary/30 transition-colors object-cover"
                  />
                  <div className="absolute inset-0 rounded-lg transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/10">
                    <span className="text-white text-xs bg-black/60 px-2 py-1 rounded-lg">
                      Click to enlarge
                    </span>
                  </div>
                </div>
              ) : (
                /* Document card */
                <a
                  href={attachment.url}
                  download={attachment.filename}
                  className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-primary/10 hover:border-primary/30 transition-colors max-w-[300px]"
                >
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-card rounded-lg text-xl">
                    {getFileIcon(attachment.content_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" title={attachment.filename}>
                      {attachment.filename}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(attachment.byte_size)}
                    </p>
                  </div>
                  <Download className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </a>
              )}
            </div>
          )
        })}
      </div>

      {/* Image lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
        >
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 p-2 bg-secondary/80 hover:bg-secondary rounded-full transition-all shadow-lg"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-6 h-6 text-secondary-foreground" />
          </button>
        </div>
      )}
    </>
  )
}
